/**
 * Subscription conversion config — Phase 1+2 (Feb 2026).
 *
 * The goal of these constants is to keep three critical numbers ($18
 * burger value, weekly-eligible box list, subscribe as default) in ONE
 * place so BoxCard, BoxDetail, Cart, Home and FAQ can't drift apart.
 */

// Value of the complimentary Wagyu burger pack shipped with every
// subscription delivery. Currently 8 × ~200g patties. Retail equivalent
// is $18 (per Nov 2025 pricing sheet). Kept as a number so we can
// compute annualised value on the fly (26 fortnightly deliveries × $18
// = $468/yr of free burgers — the number that actually shifts conversion).
export const WAGYU_BURGER_VALUE = 18;

// Every subscription frequency the storefront exposes. Ordered
// weekly → fortnightly → monthly so the segmented selector reads
// left-to-right most-frequent → least-frequent.
export const FREQUENCY_OPTS = [
  { key: "weekly",      short: "Weekly",       long: "Weekly",       days: 7,  popular: false },
  { key: "fortnightly", short: "Every 2 wks",  long: "Every 2 weeks", days: 14, popular: true  },
  { key: "monthly",     short: "Every 4 wks",  long: "Every 4 weeks", days: 28, popular: false },
];

// Default frequency preselected on every box card. Every-2-weeks tests
// best (matches average protein consumption for a 2-adult household).
export const DEFAULT_FREQUENCY = "fortnightly";

// Weekly is only offered on boxes small enough to be eaten in ~1 week.
// Family Feast (13.7kg), Carnivore Collection, Grill Master's and Braai
// are all too large for realistic weekly consumption and would freeze
// out.  This gate is client-side (we render fewer buttons) — the backend
// still accepts weekly universally to keep the API surface simple.
export const WEEKLY_ELIGIBLE_BOX_SLUGS = new Set([
  "starter-box",
  "gluten-free-box",
  "performance-box",
  // BYO is a special-case: it ships as a "byo-box" or "byo" slug at
  // checkout; both are accepted here so either flow lights up Weekly.
  "byo-box",
  "byo",
]);

/**
 * Return the frequency options that a given box should render.
 * Boxes not in WEEKLY_ELIGIBLE get [fortnightly, monthly].
 */
export function frequenciesForBox(slug) {
  const eligible = WEEKLY_ELIGIBLE_BOX_SLUGS.has(slug);
  return eligible
    ? FREQUENCY_OPTS
    : FREQUENCY_OPTS.filter((f) => f.key !== "weekly");
}

/**
 * How many deliveries a given cadence produces in a year. Used for the
 * "over a year that's $468 in free burgers" copy that leads the value
 * hierarchy per the Feb 2026 conversion push.
 */
export function deliveriesPerYear(freqKey) {
  const days = FREQUENCY_OPTS.find((f) => f.key === freqKey)?.days || 14;
  return Math.round(365 / days);
}

/**
 * Annualised value of the free-burgers benefit for a given cadence.
 * Weekly = $936, Fortnightly = $468, Monthly = $234.
 */
export function annualBurgerValue(freqKey) {
  return deliveriesPerYear(freqKey) * WAGYU_BURGER_VALUE;
}

/**
 * Annualised subscription discount value for a given box + cadence.
 * Used alongside `annualBurgerValue` in the "over a year that's..." copy.
 */
export function annualDiscountValue(box, freqKey) {
  const perOrderSavings = Number(box?.price || 0) - Number(box?.subscription_price || 0);
  return Math.round(perOrderSavings * deliveriesPerYear(freqKey));
}
